import $ from "jquery";
import axios from "axios";
import React from "react";
import ReactDOM from "react-dom";
import toastr from "@/lib/notifications";
import store from "./learning_strategists/state/store";
import App from "./learning_strategists/components/app";
import { getQueryStringHash } from "./learning_strategists/nav";
import "./learning_strategists/state/reactions";
import { checkAuthentication, redirectToLogin } from "./lib/auth-check";
import { setCookie, getCookie } from "./lib/cookies";
import AuthLoading from "./components/AuthLoading";

// All app styling: Tailwind preflight/theme/utilities, shadcn tokens, and the
// few legacy component classes ported into @layer components.
import "./styles/tailwind.css";

// Google Maps loader functions
window.loadScript = function(src, callback) {
  var script = document.createElement('script')
  script.src = src;
  script.type = 'text/javascript';
  script.onreadystatechange = function () {
    if (this.readyState == 'complete') callback();
  },
  script.onload = callback;
  document.getElementsByTagName("head")[0].appendChild(script);
}

window.loadGoogleMapsScript = function(callback) {
  // Load Google JSAPI first if not loaded
  if (typeof google === 'undefined') {
    window.loadScript('https://www.google.com/jsapi', function() {
      google.load("maps", "3", {
        callback: callback,
        other_params: "key=" + import.meta.env.VITE_GOOGLE_MAPS_API_KEY + "&libraries=places,geometry&region=ZA&async=2"
      });
    });
  } else {
    google.load("maps", "3", {
      callback: callback,
      other_params: "key=" + import.meta.env.VITE_GOOGLE_MAPS_API_KEY + "&libraries=places,geometry&region=ZA&async=2"
    });
  }
}

// Global error handlers
window.handleError = function (error) {
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.custom_error_message
  ) {
    toastr.error(error.response.data.custom_error_message);
  } else {
    toastr.error("something went wrong");
  }
};

window.showMessage = function (message) {
  toastr.success(message);
};


// Get API base URL from environment
const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_ENDPOINT;
};

// Handle OAuth callback from accounts service
function handleOAuthCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get('access_token');
  const service = urlParams.get('service');

  if (!accessToken) {
    toastr.error('No access token received');
    redirectToLogin();
    return;
  }

  // Store the access token in a cookie
  setCookie('X-User-Access-Token', accessToken, 365);

  // Redirect to the service URL or home
  const redirectUrl = service || '/';
  window.location.href = redirectUrl;
}

// Initialize app with user data
const initializeApp = async (user_data) => {
  // Get access token from cookie (it's stored there by auth)
  const accessToken = getCookie('X-User-Access-Token');

  // Configure axios defaults
  const apiBaseUrl = getApiBaseUrl();
  axios.defaults.baseURL = apiBaseUrl;
  axios.defaults.headers.common["X-User-Access-Token"] = accessToken;

  // Configure jQuery ajax defaults
  $.ajaxSetup({
    headers: {
      "X-User-Access-Token": accessToken,
    },
    beforeSend: function (xhr, settings) {
      if (!settings.url.match(/^https?:\/\//)) {
        settings.url = apiBaseUrl + settings.url;
      }
    },
  });

  // Initial main view comes from localStorage (a per-user UI preference);
  // the URL only carries the loaded entity (e/l/t/lpt). Default is the
  // split-pane backlog so existing users aren't dropped into the grid.
  const initial_query = getQueryStringHash()
  let initial_main_view = 'backlog'
  try {
    const stored = localStorage.getItem('main_view')
    if (stored === 'pipeline' || stored === 'tasks') {
      initial_main_view = stored
    }
  } catch (e) { /* ignore */ }

  // Initialize state
  const initialState = {
    backlog: {},
    backlog_item_selection: null,
    search: {
      results: [],
      loading: false,
    },
    enquiry: null,
    orders: [],
    change_applied: false,
    tutor_results: [],
    tutor_proposals: [],
    user: user_data,
    scheduled_calls: [],
    calendar_events: [],
    pricing_calculator: null,
    pipeline: {
      loading: false,
      loading_more: false,
      loaded: false,
      assignment_required: false,
      terminal_counts_available: false,
      journeys: [],
      stage_columns: [],
      stage_counts: {},
      manager_options: [],
      manager_id: null,
      coordinator_options: [],
      coordinator_id: null,
      include_unclaimed: false,
      include_archived: false,
      // Closed / Handed Over columns are scoped to this window; defaults are
      // set when the API responds (last 30 days), but pre-seed today so the
      // initial picker UI isn't blank.
      terminal_from: null,
      terminal_to: null,
      current_tab: 'all_assigned',
      offset: 0,
      per_page: 50,
      has_more: false,
    },
    tasks_overview: {
      loaded: false,
      loading: false,
      tasks: [],
      page: 1,
      more_results: false,
      user_filter: 'mine',
      filter: 'today',
      search: '',
      filter_options: [],
      user_filter_options: [],
      assignees: [],
    },
    ui: {
      main_view: initial_main_view,
      // Within the pipeline section, are we looking at the grid table or the
      // open enquiry/lead full-width. Default 'detail' if we landed mid-entity
      // (pipeline preference + e=/l= in URL); otherwise 'table' so clicking
      // the grid icon always returns to the grid.
      pipeline_view:
        initial_main_view === 'pipeline' && (initial_query.e || initial_query.l)
          ? 'detail'
          : 'table',
      // Same idea as pipeline_view: within the Tasks section, are we looking
      // at the list of tasks or at an opened enquiry/lead detail panel.
      tasks_view:
        initial_main_view === 'tasks' && (initial_query.e || initial_query.l)
          ? 'detail'
          : 'list',
      enquiry: {
        id: null,
        tab_key: "activity",
        lesson_plan_id: null,
        lesson_plan_tab: getQueryStringHash().lpt || 'lesson_setup',
        tutor_id: null,
        smser: {
          open: false,
          content: "",
          enquiry_id: null,
          recipient_name: "",
          do_not_contact: false,
        },
        buttons: {
          support_handover: {
            open: false,
          },
          deactivate: {
            open: false,
          },
          reactivate: {
            open: false,
          },
        },
      },
      volume: localStorage.getItem("volume") || 0.5,
      soundOption: localStorage.getItem("soundOption") || "ping.wav",
      lead: {
        id: null,
        tab_key: "activity",
        lesson_plan_id: null,
        tutor_id: null,
        smser: {
          open: false,
          content: "",
          lead_id: null,
          recipient_name: "",
          do_not_contact: false,
        },
        buttons: {
          sales_handover: {
            open: false,
          },
          deactivate: {
            open: false,
          },
          reactivate: {
            open: false,
          },
        },
      },
      tutoring_job: {
        loading_tutors: true,
        tutor_results_filter: "default",
        tutor_experience_level: "any",
        tutor_active_within: "6_months",
        onboarded_after: "all",
        tutor_search_bio: "",
      },
      tutor_searcher: {
        tutoring_job_id: "",
      },
      tutor_proposer: {
        viewing_tutor_id: "",
        tutoring_job_id: "",
      },
      tutor_acceptor: {
        viewing_tutor_id: "",
        tutoring_job_id: "",
      },
      tutor_rejector: {
        viewing_tutor_id: "",
        tutoring_job_id: "",
      },
      tutor_smser: {
        tutor_id: "",
        tutor_name: "",
        tutoring_job_id: "",
        content: "",
      },
      mailer: {
        open: false,
        related_to_type: "",
        related_to_id: "",
        recipient_name: "",
        template_name: "",
        templates: [],
        email: {},
      },
      whatsapper: {
        open: false,
        related_to_type: "",
        related_to_id: "",
        recipient_type: "",
        recipient_id: "",
        recipient_name: "",
        template_name: "",
        templates: [],
        whatsapp: {},
      },
      new_lead: {
        open: false,
        saving: false,
        countries: [],
        subject_categories: {},
      },
      request_placement: {
        tutoring_job_id: "",
        request_tutor_search: true,
        position_ids: [],
        placement_manager_id: "",
        placement_managers: [],
        reserved_for_minutes: 240,
      },
      complete_placement: {
        tutoring_job_id: "",
        placement_request_id: "",
        cancellation_reasons: [],
      },
      handing_over_enquiry: false,
      deactivating_enquiry: false,
      loading_unclaimed_backlog: true,
      google_map: {
        loaded: false,
      },
      messages: {
        view: "",
        page: 1,
      },
      scheduled_calls: {
        open: false,
        loading: false,
        subscribed: false,
      },
      calendar_events: {
        open: false,
        loading: false,
        failed: false,
      },
      notifications: {
        unreads: false,
      },
    },
  };

  store.get().set(initialState);

  // Set up store update handler
  store.on("update", function (currentState, prevState) {
    ReactDOM.render(
      React.createElement(App, store.get()),
      document.getElementById("app-container"),
    );
  });

  // Initial render
  ReactDOM.render(
    React.createElement(App, store.get()),
    document.getElementById("app-container"),
  );

  // Set up Pusher if user has it configured
  const user = store.get().user;
  if (user.pusher) {
    if (typeof window.SharedWorker === "undefined") {
      console.log("Your browser does not support SharedWorkers");
    } else {
      var sharedPusherWorker = new SharedWorker("/shared-pusher-worker.js?v=4");
      sharedPusherWorker.port.start();

      sharedPusherWorker.port.onmessage = function (e) {
        var data = e.data;

        if (data.event === "ping") {
          showMessage("Ping!");
        } else if (data.event === "update-unclaimed-backlog") {
          store.get().backlog.set(data.data);
        } else if (data.event === "inbox:notification") {
          store.emit("inbox:notification", data.data);
        } else if (data.event === "notification") {
          new NotificationActions().receive(data.data);
        }
      };

      sharedPusherWorker.port.onerror = function (error) {
        console.log(error);
        sharedPusherWorker.port.close();
      };

      sharedPusherWorker.port.postMessage({
        user: user,
        session: { access_token: accessToken },
      });
    }

    if (Notification) {
      if (Notification.permission === "default") {
        Notification.requestPermission(function (status) {
          // Permission requested
        });
      }
    }
  }

  // Handle query string routing
  const query_string_hash = getQueryStringHash();

  // If the URL has an entity we're going to land on the detail view; skip the
  // pipeline:index fetch so we don't flash the grid and then immediately
  // discard the request. The fetch fires later via ui:main_view:set when the
  // user toggles to the grid.
  const landing_on_detail = !!(query_string_hash.e || query_string_hash.l);

  if (initial_main_view === 'pipeline' && !landing_on_detail) {
    store.emit("pipeline:index");
  }

  if (query_string_hash.e) {
    if (query_string_hash.t) {
      store.emit("enquiries:show", query_string_hash.e, query_string_hash.t);
    } else {
      store.emit("enquiries:show", query_string_hash.e);
    }
  } else if (query_string_hash.l) {
    store.emit("leads:show", query_string_hash.l);
  }
};

// Function to render loading state
function renderLoading() {
  const container = document.getElementById('app-container')
  if (container) {
    ReactDOM.render(React.createElement(AuthLoading), container)
  }
}

// Function to load and render the auth page
async function loadAuthPage() {
  const { default: Auth } = await import('./components/auth/Auth')

  const container = document.getElementById('app-container')
  if (!container) return

  // Handle successful authentication - just redirect to home
  const handleAuthSuccess = async (accessToken) => {
    window.location.href = '/'
  }

  ReactDOM.render(React.createElement(Auth, { onSuccess: handleAuthSuccess }), container)
}

// Main entry point
document.addEventListener("DOMContentLoaded", async () => {
  const path = window.location.pathname;

  // Auth page doesn't require authentication
  if (path === '/auth') {
    await loadAuthPage()
    return
  }

  // Handle OAuth callback (legacy - for external accounts service)
  if (path === '/sessions/new') {
    handleOAuthCallback();
    return;
  }

  // For all other routes, check authentication first
  renderLoading()

  // Check authentication
  const authResult = await checkAuthentication();

  if (!authResult.authenticated) {
    redirectToLogin();
    return;
  }

  // Initialize app with user data
  initializeApp(authResult.user);
});
